/**
 * gift-picker.js — Selecteur cadeau housses (1 variante = lot de 2).
 * lmdh, 18 septembre 2026 : ouvert depuis une ligne cadeau (data-gift-replace),
 * il retire d'abord l'ancien lot puis ajoute le nouveau — « changer de coloris ».
 */

(() => {
  'use strict';

  const LMH = (window.LMH = window.LMH || {});
  if (LMH.giftPickerBound) return;
  LMH.giftPickerBound = true;

  const root = window.Shopify?.routes?.root || '/';
  const picker = () => document.querySelector('[data-gift-picker]');

  const getSelection = (el) => el._giftSelection || null;

  const setSelection = (el, item) => {
    el._giftSelection = item;
  };

  const updateUI = (el) => {
    const selected = getSelection(el);
    const confirm = el.querySelector('[data-gift-confirm]');

    el.querySelectorAll('[data-gift-variant]').forEach((btn) => {
      const active = selected && Number(btn.dataset.giftVariant) === selected.id;
      btn.setAttribute('aria-pressed', active ? 'true' : 'false');
      btn.classList.toggle('is-selected', Boolean(active));
    });

    if (confirm) {
      const replacing = Boolean(el._giftReplaceKey);
      const labelSelected = replacing && el.dataset.labelReplace ? el.dataset.labelReplace : confirm.dataset.labelSelected;
      if (selected && labelSelected) {
        confirm.textContent = labelSelected.replace(/__color__/g, selected.label);
      } else {
        // lmdh 12/10/2026 (U71, lot AN) : sans choix, le bouton reste actif et dit ce qui manque (regle U23)
        confirm.textContent = confirm.dataset.labelMissing || confirm.dataset.labelIdle || confirm.textContent;
      }
      confirm.disabled = false;
    }
    el.querySelector('[data-gift-grid]')?.classList.remove('is-a-choisir');
  };

  // lmdh 12/10/2026 (U71, lot AN) : « Choisissez un coloris » touche sans choix -> la liste revient en haut et se
  // signale ; au clavier, le focus va au premier coloris disponible
  const montrerColoris = (el) => {
    const grille = el.querySelector('[data-gift-grid]');
    if (!grille) return;
    const corps = el.querySelector('.gift-picker__body');
    const doux = !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (corps) corps.scrollTo({ top: 0, behavior: doux ? 'smooth' : 'auto' });
    grille.classList.remove('is-a-choisir');
    void grille.offsetWidth;
    grille.classList.add('is-a-choisir');
    window.clearTimeout(Number(grille.dataset.minuteur));
    grille.dataset.minuteur = String(window.setTimeout(() => grille.classList.remove('is-a-choisir'), 2400));
    if (LMH.clavierRecent?.()) grille.querySelector('[data-gift-variant]:not([disabled])')?.focus({ preventScroll: true });
  };

  const selectVariant = (el, button) => {
    if (button.dataset.giftAvailable === 'false' || button.disabled) return;

    setSelection(el, {
      id: Number(button.dataset.giftVariant),
      label: button.dataset.giftLabel || '',
    });
    updateUI(el);
  };

  // lmdh 08/10/2026 (critique du panier, lot Z) : au changement de coloris, le coloris actuel est dit sur sa carte
  const marquerActuel = (el, id) => {
    el.querySelectorAll('[data-gift-variant]').forEach((btn) => {
      const meta = btn.querySelector('.gift-picker__choice-meta');
      if (!meta) return;
      if (meta.dataset.libelle === undefined) meta.dataset.libelle = meta.textContent;
      const actuel = Boolean(id) && btn.dataset.giftVariant === String(id);
      meta.textContent = actuel ? 'Tu color actual' : meta.dataset.libelle;
      btn.classList.toggle('is-current', actuel);
    });
  };

  const confirmSelection = async (el) => {
    const selected = getSelection(el);
    if (!selected?.id) return;

    const confirm = el.querySelector('[data-gift-confirm]');
    if (confirm) {
      confirm.disabled = true;
      confirm.classList.add('is-busy');
    }

    const sectionIds =
      typeof LMH.getCartSectionIds === 'function' ? LMH.getCartSectionIds() : [];

    const remplace = Boolean(el._giftReplaceKey);
    try {
      if (el._giftReplaceKey) {
        const removed = await fetch(`${root}cart/change.js`, {
          method: 'POST',
          headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: el._giftReplaceKey, quantity: 0 }),
        });
        if (!removed.ok) throw new Error(LMH.strings?.cartError || '');
        el._giftReplaceKey = null;
      }

      const response = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          items: [
            {
              id: selected.id,
              quantity: 1,
              properties: { _gift: 'true' },
            },
          ],
          sections: sectionIds.join(','),
          sections_url: `${root}cart`,
        }),
      });

      const payload = await response.json();
      if (!response.ok) {
        throw new Error(payload.description || payload.message || '');
      }

      document.dispatchEvent(
        new CustomEvent('lmh:cart:added', {
          detail: { item: payload, sections: payload.sections || null, cartPublished: false, remplace },
        })
      );

      setSelection(el, null);
      updateUI(el);
      // lmdh 08/10/2026 (critique du panier, lot Z) : le lien qui avait ouvert la fenetre a ete re-rendu ; le focus revient
      // au « Changer de coloris » de la nouvelle ligne offerte (il retombait sur la page)
      const retour =
        document.querySelector('#cart-drawer[open] [data-gift-line] [data-gift-open]') ||
        document.querySelector('[data-cart-page] [data-gift-line] [data-gift-open]');
      const fenetre = document.getElementById('gift-picker');
      if (fenetre && retour) fenetre._returnFocus = retour;
      LMH.closeDialog?.('gift-picker');
      const next = el._reminderHref;
      setReminder(el, null);
      if (next) window.location.assign(next);
    } catch (error) {
      LMH.toast?.(error.message || LMH.strings?.cartError || '', 'error');
    } finally {
      if (confirm) {
        confirm.classList.remove('is-busy');
        updateUI(el);
      }
    }
  };

  /* lmdh 28/09/2026 (P204) : rappel avant paiement quand le seuil est atteint sans cadeau choisi */
  const setReminder = (el, href) => {
    el._reminderHref = href || null;
    const skip = el.querySelector('[data-gift-skip]');
    if (skip) skip.hidden = !href;
  };


  // lmdh 09/10/2026 (critique des coussins, choix du proprietaire 4 a) : le coloris deja au panier en tete de la liste,
  // dit « Assorti a votre panier » (un meme nom = une meme teinte dans tout le catalogue). L'ordre d'origine est retabli
  // a chaque ouverture.
  const assortirAuPanier = async (el) => {
    const grille = el.querySelector('[data-gift-grid]');
    if (!grille) return;
    const lignes = [...grille.children];
    lignes.forEach((li, i) => { if (li.dataset.ordre === undefined) li.dataset.ordre = String(i); });
    lignes.sort((a, b) => Number(a.dataset.ordre) - Number(b.dataset.ordre)).forEach((li) => grille.appendChild(li));
    grille.querySelectorAll('.gift-picker__assorti').forEach((s) => s.remove());
    const coloris = [];
    try {
      const panier = await (await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } })).json();
      for (const it of panier.items || []) {
        if (it.properties && it.properties._gift) continue;
        for (const o of it.options_with_values || []) {
          if (/couleur|coloris|color/i.test(String(o.name || ''))) coloris.push(String(o.value || '').trim().toLowerCase());
        }
      }
    } catch (e) {
      return;
    }
    if (!coloris.length) return;
    const tete = [...grille.children].filter((li) => {
      const b = li.querySelector('[data-gift-variant]');
      return b && b.dataset.giftAvailable !== 'false' && coloris.includes(String(b.dataset.giftLabel || '').trim().toLowerCase());
    });
    tete.reverse().forEach((li) => {
      grille.insertBefore(li, grille.firstChild);
      const corps = li.querySelector('.gift-picker__choice-body');
      if (corps) {
        const s = document.createElement('span');
        s.className = 'gift-picker__assorti';
        s.textContent = 'Abbinato al tuo carrello';
        corps.insertBefore(s, corps.firstChild);
      }
    });
    const corpsFenetre = el.querySelector('.gift-picker__body');
    if (tete.length && corpsFenetre) corpsFenetre.scrollTop = 0;
  };

  LMH.openGiftReminder = (href) => {
    const el = picker();
    if (!el || !href) return false;
    setSelection(el, null);
    el._giftReplaceKey = null;
    marquerActuel(el, null);
    setReminder(el, href);
    const title = el.querySelector('[data-gift-title]');
    if (title && el.dataset.titleReminder) title.textContent = el.dataset.titleReminder;
    updateUI(el);
    LMH.openDialog?.('gift-picker');
    assortirAuPanier(el);
    return true;
  };

  document.addEventListener('click', (event) => {
    const openBtn = event.target.closest('[data-gift-open]');
    if (openBtn) {
      event.preventDefault();
      const el = picker();
      if (!el) {
        const href = openBtn.getAttribute('href');
        if (href) window.location.assign(href);
        return;
      }
      setSelection(el, null);
      el._giftReplaceKey = openBtn.dataset.giftReplace || null;
      marquerActuel(el, el._giftReplaceKey ? openBtn.dataset.giftCurrent : null);
      setReminder(el, null);
      const title = el.querySelector('[data-gift-title]');
      if (title) {
        title.textContent = el._giftReplaceKey && el.dataset.titleReplace ? el.dataset.titleReplace : el.dataset.titlePick || title.textContent;
      }
      updateUI(el);
      LMH.openDialog?.('gift-picker');
      assortirAuPanier(el);
      return;
    }

    const el = event.target.closest('[data-gift-picker]');
    if (!el) return;

    if (event.target.closest('[data-gift-skip-go]')) {
      event.preventDefault();
      const href = el._reminderHref;
      setReminder(el, null);
      LMH.closeDialog?.('gift-picker');
      if (href) window.location.assign(href);
      return;
    }

    const choice = event.target.closest('[data-gift-variant]');
    if (choice) {
      event.preventDefault();
      selectVariant(el, choice);
      return;
    }

    const confirm = event.target.closest('[data-gift-confirm]');
    if (confirm) {
      event.preventDefault();
      if (!getSelection(el)?.id) {
        montrerColoris(el);
        return;
      }
      confirmSelection(el);
    }
  });
})();
